// Phase I — Read-only email send log (super-admin only).
//
// Lists every entry the platform has written to the ``email_send_log``
// collection (mock outbox writes, real Resend sends, fail-loud drops, and
// retry-outbox safety-net captures). The mock-outbox page at
// ``/admin/mock-emails`` remains the place to see/open mock email bodies;
// this page is the audit trail across both providers.

import React, { useCallback, useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, AlertCircle, CheckCircle2, Mail, Send } from "lucide-react";

const STATUS_BADGE = {
    sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-900 border-emerald-200", Icon: CheckCircle2 },
    queued: { label: "Queued (mock)", cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: Mail },
    failed: { label: "Failed", cls: "bg-red-100 text-red-900 border-red-200", Icon: AlertCircle },
    retry_outbox: { label: "Retry outbox", cls: "bg-amber-100 text-amber-900 border-amber-200", Icon: AlertCircle },
};

function fmtTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminEmailLogPage() {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/email-send-log", { params: { limit: 100 } });
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <DashboardShell>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
                            <Send size={20} className="text-blue-900" />
                            Email send log
                        </h1>
                        <p className="text-sm text-slate-600">
                            Audit trail of every outbound email — mock and Resend, success and failure.
                        </p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        data-testid="email-log-refresh"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin mr-1.5" : "mr-1.5"} />
                        Refresh
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Last {items.length} entries</CardTitle>
                        <CardDescription>Newest first. Read-only.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <p className="text-sm text-slate-500">Loading…</p>
                        ) : items.length === 0 ? (
                            <p className="text-sm text-slate-500" data-testid="email-log-empty">
                                No emails have been sent yet.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="email-log-table">
                                    <thead>
                                        <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                            <th className="py-2 pr-4">Time</th>
                                            <th className="py-2 pr-4">Recipient</th>
                                            <th className="py-2 pr-4">Subject preview</th>
                                            <th className="py-2 pr-4">Provider</th>
                                            <th className="py-2 pr-4">Status</th>
                                            <th className="py-2 pr-4">Reference</th>
                                            <th className="py-2 pr-0">Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((it) => {
                                            const badge = STATUS_BADGE[it.status] || STATUS_BADGE.failed;
                                            const Icon = badge.Icon;
                                            const ref = it.resend_id || it.outbox_id || "—";
                                            return (
                                                <tr key={it.id} className="border-b border-slate-100 align-top">
                                                    <td className="py-2 pr-4 text-xs text-slate-600 whitespace-nowrap">{fmtTime(it.sent_at)}</td>
                                                    <td className="py-2 pr-4 text-slate-900 font-mono text-xs break-all" data-testid={`email-log-row-${it.id}-to`}>{it.to}</td>
                                                    <td className="py-2 pr-4 text-slate-800 max-w-md">
                                                        <span className="line-clamp-2">{it.subject_preview || "—"}</span>
                                                    </td>
                                                    <td className="py-2 pr-4">
                                                        <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">
                                                            {it.provider}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 pr-4">
                                                        <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${badge.cls}`}
                                                            data-testid={`email-log-row-${it.id}-status`}>
                                                            <Icon size={11} /> {badge.label}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 pr-4 text-[11px] font-mono text-slate-500 break-all">{ref}</td>
                                                    <td className="py-2 pr-0 text-xs text-red-700 max-w-sm">
                                                        {it.error ? <span className="line-clamp-3" title={it.error}>{it.error}</span> : <span className="text-slate-400">—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardShell>
    );
}
